import * as React from 'react';
import { memo, useCallback } from 'react';
import {
  Box,
  StackLayout,
  styled,
  useMediaQuery,
  queryHelpers,
} from '@leagueplatform/genesis-core';
import {
  ImpersonationTopBanner,
  AppBarHeader,
} from '@leagueplatform/web-common-components';
import { reportAgentPermissionError } from '@leagueplatform/core';
import { logout } from 'utils/auth/logout';
import { isEditRestricted } from 'utils/is-edit-restricted';
import { AccountModals } from 'components/modals/account-modals.component';
import { usePersonalInformation } from 'hooks/use-personal-information.hook';
import { useNavBarLogic } from 'hooks/use-navbar-logic.hook';
import { useNavLinksList } from 'hooks/useNav-links.hook';
import { useModal } from 'hooks/use-modal.hook';
import cignaLogo from 'assets/cigna-logo.svg';
import { MainNav } from './main-nav.component';
import { MobileNavWrapper } from './mobile-nav/mobile-nav-wrapper.component';

const Logo = styled('img', {
  width: '175px',
  height: '29px',
  margin: 'auto 70px auto 32px',
});

const APP_NAV_DEFAULT_HEIGHT = 64;

export const AppNavBarComponent: React.FC = memo(() => {
  const { showClaims } = usePersonalInformation();
  const { navLinks } = useNavLinksList(showClaims);
  const { menuOpen, toggleMenu, closeMenu, handleLogoClick } = useNavBarLogic();
  const isAboveLaptop = useMediaQuery(queryHelpers.up('laptop'));

  const {
    isShowing: showDeleteAccountModal,
    toggle: toggleDeleteAccountModal,
  } = useModal();
  const {
    isShowing: showAccountDeletedModal,
    toggle: toggleAccountDeletedModal,
  } = useModal();

  const handleLogout = () => logout(window.location.origin);

  const handleDeleteAccount = useCallback(() => {
    if (isEditRestricted()) {
      reportAgentPermissionError(true, undefined, 'agent');
      return;
    }
    toggleDeleteAccountModal();
  }, [toggleDeleteAccountModal]);

  return (
    <>
      <StackLayout
        orientation="horizontal"
        horizontalAlignment="spaceEvenly"
        verticalAlignment="center"
        css={{
          position: 'sticky',
          top: 0,
          width: '100vw',
          left: 0,
          zIndex: '$toast',
        }}
      >
        <ImpersonationTopBanner />
      </StackLayout>

      <Box
        css={{
          '> header > nav > ul > li > a[aria-current="page"]::after': {
            backgroundColor: '$interactiveActionPrimary',
          },
        }}
      >
        <AppBarHeader navHeight={APP_NAV_DEFAULT_HEIGHT}>
          <Logo src={cignaLogo} alt="Cigna Logo" onClick={handleLogoClick} />
          {isAboveLaptop ? (
            <MainNav
              navLinks={navLinks}
              handleDeleteAccount={handleDeleteAccount}
            />
          ) : (
            <MobileNavWrapper
              navLinks={navLinks}
              menuOpen={menuOpen}
              toggleMenu={toggleMenu}
              closeMenu={closeMenu}
              handleDeleteAccount={handleDeleteAccount}
            />
          )}
        </AppBarHeader>
        <AccountModals
          onLogout={handleLogout}
          showDeleteAccountModal={showDeleteAccountModal}
          toggleDeleteAccountModal={toggleDeleteAccountModal}
          showAccountDeletedModal={showAccountDeletedModal}
          toggleAccountDeletedModal={toggleAccountDeletedModal}
        />
      </Box>
    </>
  );
});

AppNavBarComponent.displayName = 'AppNavBarComponent';
